import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router"

import type { Meter } from "@/models/meter"
import { AdminEntityLink } from "@/modules/admin/components/admin-entity-link"
import { baseColumnDef } from "@/nzds/data-grid"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { useMeterCodeColumn } from "@/utils/grid-columns/use-meter-code-column"
import { useSiteNameColumn } from "@/utils/grid-columns/use-site-name-column"
import { useUtilityProviderColumn } from "@/utils/grid-columns/use-utility-provider-column"
import { useUrl } from "@/utils/url"
import { getDisplayValue } from "@/utils/value"

import { Link as MuiLink } from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro"

import { MeterStatusChip } from "./meter-status-chip"

export interface MeterGridRecord {
  accountNumber: string
  code: Meter["meterCode"]
  emissionFactorDisplay: Meter["emissionFactorDisplay"]
  id: string
  provider: Meter["meterProvider"]
  siteId: string | null
  siteName: string
  sourceTypeName: string
  status: Meter["meterStatus"] | null
}

export type AdminMetersGridColumnField =
  | "accountNumber"
  | "code"
  | "emissionFactorDisplay"
  | "provider"
  | "siteName"
  | "sourceTypeName"
  | "status"

interface UseAdminMetersGridColumnsOptions {
  fields: AdminMetersGridColumnField[]
  filterable?: boolean
  preserveSearch?: boolean
}

export const meterToGridRecord = (meter: Meter): MeterGridRecord => ({
  accountNumber: getDisplayValue(meter.accountNumber),
  code: getDisplayValue(meter.meterCode),
  emissionFactorDisplay: meter.emissionFactorDisplay ?? null,
  id: String(meter.id),
  provider: getDisplayValue(meter.meterProvider),
  siteId: meter.site?.id ?? null,
  siteName: getDisplayValue(meter.site?.name),
  sourceTypeName: getDisplayValue(meter.meterSourceType?.name),
  status: meter.meterStatus ?? null,
})

export const useAdminMetersGridColumns = ({
  fields,
  filterable = false,
  preserveSearch = false,
}: UseAdminMetersGridColumnsOptions): GridColDef<MeterGridRecord>[] => {
  const { t } = useTranslation("common")
  const { search } = useLocation()
  const meterLinkSearch = preserveSearch ? search : ""
  const { adminMeterPath, adminSitePath } = useUrl()
  const meterCodeColumn = useMeterCodeColumn<MeterGridRecord>({
    field: "code",
    filterable,
  })
  const siteNameColumn = useSiteNameColumn<MeterGridRecord>({
    field: "siteName",
    filterable,
  })
  const utilityProviderColumn = useUtilityProviderColumn<MeterGridRecord>({
    field: "provider",
    filterable,
  })

  const columnsByField = React.useMemo(
    (): Record<AdminMetersGridColumnField, GridColDef<MeterGridRecord>> => ({
      accountNumber: {
        ...baseColumnDef,
        field: "accountNumber",
        filterable: false,
        flex: 1,
        headerName: t("editMeter.accountNumberLabel"),
        minWidth: 120,
      },
      code: {
        ...meterCodeColumn,
        renderCell: ({ row, value }) => (
          <MuiLink
            component={Link}
            to={`${adminMeterPath(row.id)}${meterLinkSearch}`}
          >
            {value}
          </MuiLink>
        ),
      },
      emissionFactorDisplay: {
        ...baseColumnDef,
        field: "emissionFactorDisplay",
        filterable: false,
        flex: 1,
        headerName: t("admin.metersGrid.emissionFactor"),
        minWidth: 120,
        valueFormatter: (value: string | null) => getDisplayValue(value),
      },
      provider: utilityProviderColumn,
      siteName: {
        ...siteNameColumn,
        renderCell: ({
          row,
          value,
        }: GridRenderCellParams<MeterGridRecord, string>) => (
          <AdminEntityLink
            label={value ?? ""}
            to={
              row.siteId && value !== EMPTY_DATA_PLACEHOLDER
                ? adminSitePath(row.siteId)
                : undefined
            }
          />
        ),
      },
      sourceTypeName: {
        ...baseColumnDef,
        field: "sourceTypeName",
        filterable: false,
        flex: 1,
        headerName: t("fields.sourceType.title"),
        minWidth: 120,
      },
      status: {
        ...baseColumnDef,
        editable: false,
        field: "status",
        filterable: false,
        headerName: t("admin.metersGrid.status"),
        minWidth: 120,
        renderCell: ({ row }) => <MeterStatusChip meterStatus={row.status} />,
        sortable: false,
        width: 120,
      },
    }),
    [
      adminMeterPath,
      adminSitePath,
      filterable,
      meterCodeColumn,
      meterLinkSearch,
      siteNameColumn,
      t,
      utilityProviderColumn,
    ]
  )

  return fields.map((field) => columnsByField[field])
}
